/**
 * Tenant management — super-admin surface for creating, listing, and
 * (soft-)removing customer workspaces.
 *
 * Only visible to users whose email is in SUPER_ADMIN_EMAILS (resolved on
 * the backend via /superadmin/whoami; the auth store carries the flag).
 *
 * Layout:
 *   • Top: create-tenant form. On success surfaces the one-time activation
 *     URL (we email it too, but the link is shown as a backup).
 *   • Bottom: list of every active tenant with a Remove button per row.
 *     Removal is soft — Postgres keeps the rows, logins just fail.
 *
 * Guardrail: a super-admin can't remove the tenant their own JWT is bound to
 * right now (the backend rejects with 409). The button is still rendered so
 * the affordance is honest — the click surfaces the message.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

type CreateTenantResponse = {
  tenant_id: string;
  slug: string;
  name: string;
  admin_user_id: string;
  admin_email: string;
  // For an admin who already has an account this is a sign-in link for the
  // new workspace (/login?workspace=<slug>), not an activation link.
  activation_url: string;
  email_sent: boolean;
  existing_account?: boolean;
};

type TenantSummary = {
  id: string;
  slug: string;
  name: string;
  plan: string;
  created_at: string;
  member_count: number;
  project_count: number;
  issue_count: number;
  open_issue_count: number;
  last_activity_at: string | null;
};

type TenantList = {
  tenants: TenantSummary[];
  total_tenants: number;
  total_members: number;
  total_open_issues: number;
};

export function SuperAdminOnboardingPage() {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const currentTenantSlug = useAuthStore((s) => s.tenant?.slug ?? null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [phone, setPhone] = useState("");
  const [mailingAddress, setMailingAddress] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [result, setResult] = useState<CreateTenantResponse | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TenantSummary | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Auto-suggest a kebab-case slug from the tenant name unless the operator
  // has typed something themselves.
  useEffect(() => {
    if (slugTouched) return;
    const suggestion = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 63);
    setSlug(suggestion);
  }, [name, slugTouched]);

  const { data: tenantList, isLoading: tenantsLoading } = useQuery({
    queryKey: ["superadmin", "tenants"],
    queryFn: async () => (await api.get<TenantList>("/superadmin/tenants")).data,
    enabled: isSuperAdmin,
  });

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post<CreateTenantResponse>("/superadmin/tenants", {
          name: name.trim(),
          slug: slug.trim(),
          // V1 ships a single plan; backend always resolves to "team".
          phone: phone.trim(),
          mailing_address: mailingAddress.trim(),
          billing_email: billingEmail.trim(),
          admin_email: adminEmail.trim(),
          admin_display_name: adminName.trim(),
        })
      ).data,
    onSuccess: (resp) => {
      setResult(resp);
      setBannerError(null);
      setCopied(false);
      setName("");
      setSlug("");
      setSlugTouched(false);
      setPhone("");
      setMailingAddress("");
      setBillingEmail("");
      setAdminEmail("");
      setAdminName("");
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
    },
    onError: (err) => {
      setBannerError(extractErrorMessage(err, "Could not create tenant."));
    },
  });

  const remove = useMutation({
    mutationFn: async (targetSlug: string) => {
      await api.delete(`/superadmin/tenants/${targetSlug}`);
    },
    onSuccess: () => {
      setRemoveTarget(null);
      setRemoveError(null);
      qc.invalidateQueries({ queryKey: ["superadmin", "tenants"] });
    },
    onError: (err) => {
      setRemoveError(extractErrorMessage(err, "Could not remove this tenant."));
    },
  });

  if (!isSuperAdmin) {
    return (
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px" }}>
          Tenant management
        </h1>
        <p style={{ color: "var(--mute)", marginTop: 8 }}>
          Super-admin only. This is the SaaS owners' surface for creating and
          removing customer workspaces.
        </p>
      </div>
    );
  }

  const tenants = tenantList?.tenants ?? [];

  return (
    <div style={{ maxWidth: 960 }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Tenant management
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Create new customer workspaces, and remove ones that have churned.
          Removals are soft — data sticks around in Postgres until purged.
        </p>
      </header>

      {result && (
        <div style={successBanner}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Created {result.name} ({result.slug}) — admin {result.admin_email}.
          </div>
          <div style={{ fontSize: 13, color: "var(--body)" }}>
            {result.existing_account
              ? result.email_sent
                ? "They already have a minute2sec account, so there's no password to set. We emailed them a sign-in link for this workspace. The copy below is a backup."
                : "They already have a minute2sec account. Email send failed — share this sign-in link for the workspace manually."
              : result.email_sent
                ? "We emailed them the activation link. The copy below is a backup in case they don't see it."
                : "Email send failed — please share this activation link manually. It expires in 24 hours."}
          </div>
          <div
            style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}
          >
            <input
              readOnly
              value={result.activation_url}
              style={{
                flex: 1,
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                padding: "6px 10px",
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderRadius: 4,
                color: "var(--ink)",
              }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(result.activation_url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              style={primaryBtn}
            >
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/superadmin/dashboard")}
              style={ghostBtn}
            >
              View dashboard
            </button>
          </div>
        </div>
      )}

      <section style={card}>
        <h2 style={cardTitle}>Create a tenant</h2>
        <label style={fieldRow}>
          <span style={fieldLabel}>Tenant name</span>
          <input
            type="text"
            placeholder="Acme Robotics"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={input}
          />
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Slug</span>
          <input
            type="text"
            placeholder="acme-robotics"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            style={{ ...input, fontFamily: "var(--font-mono)" }}
            pattern="^[a-z][a-z0-9-]*[a-z0-9]$"
          />
          <span style={hint}>
            Lowercase letters, digits, hyphens. Used to identify the tenant in
            audit logs and admin URLs. All tenants log in at the same host —
            <code> app.minute2sec.com</code>.
          </span>
        </label>
        <div style={sectionDivider}>Consultancy contact</div>

        <label style={fieldRow}>
          <span style={fieldLabel}>Phone</span>
          <input
            type="tel"
            placeholder="+1 415 555 0188"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={input}
            autoComplete="off"
          />
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Mailing address</span>
          <textarea
            placeholder="123 Market Street, Suite 400&#10;San Francisco, CA 94103"
            value={mailingAddress}
            onChange={(e) => setMailingAddress(e.target.value)}
            style={{ ...input, minHeight: 64, resize: "vertical", fontFamily: "var(--font-sans)" }}
          />
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Billing email</span>
          <input
            type="email"
            placeholder="ap@acme.com"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            style={input}
            autoComplete="off"
          />
          <span style={hint}>Where invoices and DPA paperwork go. Can match the admin email.</span>
        </label>

        <div style={sectionDivider}>First admin</div>

        <label style={fieldRow}>
          <span style={fieldLabel}>Admin email</span>
          <input
            type="email"
            placeholder="founder@acme.com"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            style={input}
            autoComplete="off"
          />
          <span style={hint}>We'll email them a one-click signup link.</span>
        </label>
        <label style={fieldRow}>
          <span style={fieldLabel}>Admin display name</span>
          <input
            type="text"
            placeholder="Sara Lee"
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            style={input}
          />
        </label>

        {bannerError && (
          <p
            role="alert"
            style={{
              marginTop: 12,
              padding: "8px 12px",
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
            }}
          >
            {bannerError}
          </p>
        )}

        <button
          type="button"
          onClick={() => {
            setBannerError(null);
            if (
              !name.trim() ||
              !slug.trim() ||
              !phone.trim() ||
              !mailingAddress.trim() ||
              !billingEmail.trim() ||
              !adminEmail.trim() ||
              !adminName.trim()
            ) {
              setBannerError("All fields are required.");
              return;
            }
            if (!/^[a-z][a-z0-9-]*[a-z0-9]$/.test(slug.trim())) {
              setBannerError("Slug must be lowercase letters, digits, and hyphens — start and end with a letter or digit.");
              return;
            }
            create.mutate();
          }}
          disabled={create.isPending}
          style={{
            ...primaryBtn,
            marginTop: 16,
            width: "100%",
            opacity: create.isPending ? 0.6 : 1,
            cursor: create.isPending ? "wait" : "pointer",
          }}
        >
          {create.isPending ? "Creating tenant…" : "Create tenant"}
        </button>
      </section>

      {/* Tenant list with Remove */}
      <section style={{ marginTop: 32 }}>
        <div style={sectionTitle}>
          Tenants · {tenantsLoading ? "…" : tenants.length}
        </div>
        {tenantsLoading ? (
          <p style={{ color: "var(--mute)" }}>Loading…</p>
        ) : tenants.length === 0 ? (
          <p style={{ color: "var(--mute)" }}>No active tenants.</p>
        ) : (
          <div
            style={{
              border: "1px solid var(--hairline)",
              borderRadius: 8,
              background: "var(--canvas)",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}
            >
              <thead>
                <tr style={{ background: "var(--surface-1)" }}>
                  <th style={th}>Name</th>
                  <th style={th}>Slug</th>
                  <th style={th}>Plan</th>
                  <th style={{ ...th, textAlign: "right" }}>Members</th>
                  <th style={{ ...th, textAlign: "right" }}>Open issues</th>
                  <th style={th}>Created</th>
                  <th style={{ ...th, width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const isCurrent = t.slug === currentTenantSlug;
                  return (
                    <tr key={t.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                      <td style={td}>{t.name}</td>
                      <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
                        {t.slug}
                      </td>
                      <td style={td}>
                        <span style={planPill}>{t.plan}</span>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {t.member_count}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {t.open_issue_count}
                      </td>
                      <td style={{ ...td, color: "var(--mute)" }}>
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => {
                            setRemoveError(null);
                            setRemoveTarget(t);
                          }}
                          disabled={isCurrent}
                          title={
                            isCurrent
                              ? "You're currently logged into this tenant"
                              : `Remove ${t.name}`
                          }
                          style={removeBtn(isCurrent)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {removeTarget && (
        <ConfirmRemoveDialog
          tenant={removeTarget}
          pending={remove.isPending}
          error={removeError}
          onCancel={() => {
            if (remove.isPending) return;
            setRemoveTarget(null);
            setRemoveError(null);
          }}
          onConfirm={() => remove.mutate(removeTarget.slug)}
        />
      )}
    </div>
  );
}

// ---------- confirm dialog ----------

function ConfirmRemoveDialog({
  tenant,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  tenant: TenantSummary;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div role="dialog" aria-modal="true" style={overlay} onClick={onCancel}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: "0 0 6px" }}>
          Remove {tenant.name}?
        </h2>
        <p style={{ color: "var(--mute)", fontSize: 13, margin: "0 0 16px", lineHeight: 1.55 }}>
          Every member of {tenant.slug} will be locked out immediately. The
          data stays in Postgres (and R2 for attachments) until you purge it
          manually, so this is reversible — clear <code>deleted_at</code> on the
          tenant row to restore.
        </p>
        {error && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              padding: "8px 12px",
              margin: "0 0 12px",
            }}
          >
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} disabled={pending} style={secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            style={{ ...dangerBtn, opacity: pending ? 0.6 : 1, cursor: pending ? "wait" : "pointer" }}
          >
            {pending ? "Removing…" : "Remove tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- styles ----------

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
};

const cardTitle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  margin: "0 0 16px",
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const sectionDivider: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  margin: "18px 0 10px",
  paddingTop: 12,
  borderTop: "1px solid var(--hairline)",
};

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const hint: CSSProperties = {
  display: "block",
  marginTop: 6,
  fontSize: 12,
  color: "var(--mute)",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const secondaryBtn: CSSProperties = {
  background: "transparent",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const dangerBtn: CSSProperties = {
  background: "#c11930",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

function removeBtn(disabled: boolean): CSSProperties {
  return {
    background: "transparent",
    color: disabled ? "var(--mute)" : "#c11930",
    border: `1px solid ${disabled ? "var(--hairline)" : "rgba(193,25,48,0.35)"}`,
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}

const ghostBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  cursor: "pointer",
};

const successBanner: CSSProperties = {
  background: "rgba(0,215,34,0.06)",
  border: "1px solid rgba(0,215,34,0.25)",
  borderRadius: 6,
  padding: "12px 16px",
  marginBottom: 16,
  fontSize: 13,
  color: "var(--ink)",
};

const th: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  padding: "10px 14px",
  borderBottom: "1px solid var(--hairline)",
};

const td: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  color: "var(--body)",
};

const planPill: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  padding: "2px 8px",
  borderRadius: 3,
  background: "var(--surface-2)",
  color: "var(--body-mid)",
};

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8,8,8,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};

const modal: CSSProperties = {
  width: "min(440px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow: "0 12px 32px rgba(8,8,8,0.18)",
};
